<template>
  <Dialog
    v-model:visible="showDialog"
    :style="dialogStyle"
    :modal="true"
    :closable="true"
    :contentStyle="{ overflowY: 'auto', paddingTop: '0' }"
    @hide="close"
  >
    <template #header>
      <div v-if="selectedDayDetails" class="custom-header">
        <div class="header-content">
          <!-- Imagen de perfil -->
          <div class="profile-image">
            <Image
              :src="
                buildProfileImageUrl(
                  selectedDayDetails.vacations[0]?.profilePicture || null
                )
              "
              alt="Profile"
              width="50"
              height="50"
              class="border-round-2xl"
            />
          </div>

          <!-- Información: Fecha y Nombre -->
          <div class="header-info">
            <div class="date-text">
              {{ formatOnlyDate(selectedDayDetails.date) }}
            </div>
            <div class="employee-name">
              {{ selectedDayDetails.vacations[0]?.firstname }}
              {{ selectedDayDetails.vacations[0]?.lastname }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <Divider class="mt-0 mb-4" />

    <div v-if="selectedDayDetails && selectedDayDetails.vacations.length > 0">
      <template
        v-for="(vacation, index) in selectedDayDetails.vacations"
        :key="index"
      >
        <!-- Separador entre items (excepto el primero) -->
        <Divider v-if="index > 0" class="my-4" />

        <!-- Tarjeta de vacación -->
        <div class="flex align-items-start gap-3 mb-3 py-3">
          <!-- Color indicator -->
          <div
            class="flex-shrink-0 w-2rem h-2rem"
            :style="{
              backgroundColor: getVacationColor(vacation),
              borderRadius: '0.25rem',
            }"
          />

          <!-- Contenido -->
          <div class="flex-grow-1">
            <!-- Título + Estado -->
            <div class="vacation-title-status my-2 md:mb-2 md:mt-0 gap-2">
              <h4 class="m-0 font-bold text-lg">
                {{ vacation.recordTitle }}
              </h4>
              <span
                class="vacation-status"
                :class="{
                  'status-pending': vacation.requestStatus === 0,
                  'status-approved': vacation.requestStatus === 1,
                  'status-completed': vacation.recordStatus === 4,
                }"
              >
                {{ getVacationStatus(vacation) }}
              </span>
            </div>

            <!-- Descripción -->
            <p
              v-if="vacation.recordInfo || vacation.requestInfo"
              class="text-sm text-600 mb-3"
            >
              {{ vacation.recordInfo || vacation.requestInfo }}
            </p>

            <!-- Fechas/Horas/vacaciones -->
            <div class="flex flex-col md:flex-row gap-4 md:gap-8 text-md">
              <div>
                <span class="text-600 block"
                  >{{ t("wkt.client.employeeCalendar.from") }}:</span
                >
                <div class="font-medium">
                  {{ formatSimpleDate(vacation.fromDate) }}
                </div>
              </div>
              <div>
                <span class="text-600 block"
                  >{{ t("wkt.client.employeeCalendar.to") }}:</span
                >
                <div class="font-medium">
                  {{ formatSimpleDate(vacation.toDate) }}
                </div>
              </div>
              <div>
                <span class="text-600 block"
                  >{{ t("wkt.client.employeeCalendar.vacation") }}:</span
                >
                <div
                  class="font-medium"
                  :style="{
                    color: vacation.hoursBagDelta != null && vacation.hoursBagDelta > 0 ? '#16a34a' : vacation.hoursBagDelta != null && vacation.hoursBagDelta < 0 ? '#dc2626' : 'inherit'
                  }"
                  v-if="vacation.hoursBagDelta != null"
                >
                  {{ `${formatHours(vacation.hoursBagDelta)} ${t("wkt.client.employeeCalendar.days")}` }}
                </div>
                <div class="font-medium" v-else>
                  {{ t("wkt.client.employeeCalendar.non_days")}}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from "primevue/dialog";
import Divider from "primevue/divider";
import Image from "primevue/image";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  RequestStatus,
  type CalendarDayData,
  type FullVacation,
} from "../../../api/employee-calendar";
import { useDateFormatter } from "../../../composables/useDateFormatter";
// Estilo responsivo para el diálogo
const dialogStyle = computed(() => {
  return {
    width: "650px",
    maxWidth: "95vw",
    maxHeight: "80vh",
    minWidth: "320px",
  };
});

// Emits
const emit = defineEmits<{
  closed: [];
}>();

// Composables
const { t } = useI18n();
const { formatOnlyDate, formatSimpleDate } = useDateFormatter();

// State
const showDialog = ref(false);
const selectedDayDetails = ref<CalendarDayData | null>(null);

// Methods
function open(data: CalendarDayData) {
  selectedDayDetails.value = data;
  showDialog.value = true;
}

function close() {
  showDialog.value = false;
  selectedDayDetails.value = null;
  emit("closed");
}

/**
 * Obtiene el color de la vacación según su tipo
 */
function getVacationColor(vacation: FullVacation): string {
  // Pendiente (amarillo)
  if (vacation.requestStatus === RequestStatus.PENDING) {
    return "#FEE8A1";
  }
  // Aprobada (verde)
  if (vacation.requestStatus === RequestStatus.APPROVED) {
    return "#A7D9D2";
  }
  // Ausencia retribuida (rosa)
  if (vacation.hoursBagType === RequestStatus.PAID_ABSENCE) {
    return "#F9DDE8";
  }
  // Vacaciones (azul claro)
  if (vacation.hoursBagType === RequestStatus.VACATION) {
    return "#AFD6FB";
  }
  // Ausencia no retribuida (púrpura)
  if (vacation.hoursBagType !== RequestStatus.VACATION) {
    return "#C5B6E5";
  }
  // Por defecto gris
  return "#9E9E9E";
}

/**
 * Obtiene el estado en texto
 */
function getVacationStatus(vacation: FullVacation): string {
  // Pendiente
  if (vacation.requestStatus === 0) {
    return t("wkt.client.employeeCalendar.status.pending") || "Pendiente";
  }
  // Aprobada
  if (vacation.requestStatus === 1) {
    return t("wkt.client.employeeCalendar.status.approved") || "Aprobada";
  }
  // Completada
  if (vacation.recordStatus === 4) {
    return t("wkt.client.employeeCalendar.status.completed") || "Completada";
  }
  return t("wkt.client.employeeCalendar.status.unknown") || "Desconocido";
}

/**
 * Formatea horas mostrando decimales solo si son necesarios
 */
function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return Number.isInteger(value) ? value.toString() : value.toFixed(2);
}

/**
 * Construye la URL de la imagen de perfil
 */
function buildProfileImageUrl(fileName: string | null | undefined): string {
  if (!fileName) return "img/default-avatar.jpeg";
  const bucketName = "worktime-dev";
  return `https://storage.googleapis.com/${bucketName}/${fileName}`;
}

// Expose methods
defineExpose({
  open,
  close,
});
</script>

<style scoped>
.vacation-title-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 600px) {
  .vacation-title-status {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
  .vacation-status {
    margin-top: 0.25rem;
    margin-left: 0;
  }
}
.custom-header {
  width: 100%;
}

@media (max-width: 600px) {
  :deep(.p-dialog) {
    width: 95vw !important;
    min-width: 0 !important;
    max-width: 95vw !important;
    margin: 0 auto !important;
    border-radius: 12px !important;
  }
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.profile-image {
  flex-shrink: 0;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  justify-content: center;
}

.employee-name {
  font-weight: 600;
  font-size: 18px;
  color: #424242;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.date-text {
  color: #757575;
  font-size: 16px;
  font-weight: 500;
}

.vacation-status {
  font-size: 16px;
  font-weight: 500;
  padding: 0.25rem 0;
  border-radius: 4px;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
}

.vacation-status.status-pending {
  color: #efad4e;
  background-color: transparent;
}

.vacation-status.status-approved {
  color: #4caf50;
  background-color: transparent;
}

.vacation-status.status-completed {
  color: #2196f3;
  background-color: transparent;
}
</style>
